import {
  setAuthToken,
  userRead,
  userAuthenticate,
  userCreate,
  userPasswordForgot,
  userPasswordReset,
  userGenerateFirebaseToken,
} from '@allevi/allevi-api-wrapper';

import Cookie from 'js-cookie';

// Detect if running on-prem
const onPremDB = (window.hostEnvironment && window.hostEnvironment === 'ON_PREM');

// Set auth token
export const setAPIToken = token => {
  setAuthToken(token);

  return {
    type: 'SET_API_TOKEN',
    token,
  };
};

// Logout User
export const logoutUser = () => {
  // Remove token from browser storage
  sessionStorage.clear();

  // Remove session token cookie
  const uiDomain = new URL(document.location.href).hostname;
  Cookie.remove('allevi-session-token', { path: '', domain: uiDomain });

  // Remove token from API wrapper
  setAuthToken(undefined);
  window.location = '/login?reason=timeout';

  return {
    type: 'LOGOUT_USER',
  };
};

// Get current user
// Log out on fail: JWT or API connection issue requires re-login
const getUserAction = () => ({
  type: 'GET_USER',
});

const getUserSuccess = user => ({
  type: 'GET_USER_SUCCESS',
  user,
});

const getUserFailure = (statusCode, message) => ({
  type: 'GET_USER_FAILURE',
  statusCode,
  message,
});

export function getUser() {
  return async (dispatch) => {
    dispatch(getUserAction());

    return userRead({})
      .then(e => {
        if (e.statusCode === 200 && e.user) {
          dispatch(getUserSuccess(e.user));
          return { success: true };
        }
        dispatch(getUserFailure(e.statusCode, e.message));
        logoutUser();
        return { success: false };
      })
      .catch(e => {
        dispatch(getUserFailure(e.statusCode, e.message));
        logoutUser();
        return { success: false };
      });
  };
}

// Authenticate user
const authenticateUserAction = (email, password) => ({
  type: 'AUTHENTICATE_USER',
  email,
  password,
});

const authenticateUserSuccess = user => ({
  type: 'AUTHENTICATE_USER_SUCCESS',
  user,
});

const authenticateUserFailure = (statusCode, message) => ({
  type: 'AUTHENTICATE_USER_FAILURE',
  statusCode,
  message,
});

export function authenticateUser(email, password) {
  return (dispatch) => {
    dispatch(authenticateUserAction(email, password));

    return userAuthenticate(email, password)
      .then(e => {
        if (e.statusCode === 200 && e.token && e.user) {
          dispatch(authenticateUserSuccess(e.user));
          // Store token in browser storage
          sessionStorage.setItem('id_token', e.token);
          // Set token for API wrapper
          setAuthToken(e.token);
          return { success: true };
        }
        dispatch(authenticateUserFailure(e.statusCode, e.message));
        return { success: false, ...e };
      })
      .catch(e => {
        dispatch(authenticateUserFailure(e.statusCode, e.message));
        return { success: false, ...e };
      });
  };
}

// // Generate temporary token
// const generateTokenAction = (email, password) => ({
//   type: 'GENERATE_TOKEN',
//   email,
//   password,
// });
//
// const generateTokenSuccess = token => ({
//   type: 'GENERATE_TOKEN_SUCCESS',
//   token,
// });
//
// const generateTokenFailure = (statusCode, message) => ({
//   type: 'GENERATE_TOKEN_FAILURE',
//   statusCode,
//   message,
// });
//
// export function generateToken() {
//   return (dispatch) => {
//     dispatch(generateTokenAction());
//
//     return userCreateToken()
//       .then(e => {
//         if (e.statusCode === 200 && e.data.token) {
//           dispatch(generateTokenSuccess(e.data.token));
//           return {
//             success: true,
//             token: e.data.token,
//           };
//         }
//         dispatch(generateTokenFailure(e.statusCode, e.message));
//         return { success: false };
//       })
//       .catch(e => {
//         dispatch(generateTokenFailure(e.statusCode, e.message));
//         return { success: false };
//       });
//   };
// }

// Generate firebase token for comms
const generateCommTokenAction = () => ({
  type: 'GENERATE_COMM_TOKEN',
});

const generateCommTokenSuccess = token => ({
  type: 'GENERATE_COMM_TOKEN_SUCCESS',
  token,
});

const generateCommTokenFailure = (statusCode, message) => ({
  type: 'GENERATE_COMM_TOKEN_FAILURE',
  statusCode,
  message,
});

export function generateCommToken() {
  return (dispatch) => {
    dispatch(generateCommTokenAction());
    if (onPremDB) {
      dispatch(generateCommTokenSuccess());
      return Promise.resolve({
        success: true,
      });
    }

    return userGenerateFirebaseToken()
      .then(e => {
        if (e.statusCode === 200 && e.token) {
          dispatch(generateCommTokenSuccess(e.token));
          return {
            success: true,
            token: e.token,
          };
        }
        dispatch(generateCommTokenFailure(e.statusCode, e.message));
        return { success: false };
      })
      .catch(e => {
        dispatch(generateCommTokenFailure(e.statusCode, e.message));
        return { success: false };
      });
  };
}

// Register user
const registerUserAction = (firstName, lastName, email, password) => ({
  type: 'REGISTER_USER',
  firstName,
  lastName,
  email,
  password,
});

const registerUserSuccess = user => ({
  type: 'REGISTER_USER_SUCCESS',
  user,
});

const registerUserFailure = (statusCode, message) => ({
  type: 'REGISTER_USER_FAILURE',
  statusCode,
  message,
});

export function registerUser(firstName, lastName, email, password) {
  return (dispatch) => {
    dispatch(registerUserAction(firstName, lastName, email, password));

    return userCreate(email, password, { first: firstName, last: lastName })
      .then(e => {
        if (e.statusCode === 200 && e.user) {
          dispatch(registerUserSuccess(e.user));
          return { success: true, user: e.user };
        }
        dispatch(registerUserFailure(e.statusCode, e.message));
        return { success: false };
      })
      .catch(e => {
        dispatch(registerUserFailure(e.statusCode, e.message));
        return { success: false };
      });
  };
}

// Forgot password
const forgotPasswordAction = email => ({
  type: 'FORGOT_PASSWORD',
  email,
});

const forgotPasswordSuccess = () => ({
  type: 'FORGOT_PASSWORD_SUCCESS',
});

const forgotPasswordFailure = (statusCode, message) => ({
  type: 'FORGOT_PASSWORD_FAILURE',
  statusCode,
  message,
});

export function forgotPassword(email) {
  return (dispatch) => {
    dispatch(forgotPasswordAction(email));

    return userPasswordForgot(email)
      .then(e => {
        if (e.statusCode === 200) {
          dispatch(forgotPasswordSuccess());
          return { success: true };
        }
        dispatch(forgotPasswordFailure(e.statusCode, e.message));
        return { success: false };
      })
      .catch(e => {
        dispatch(forgotPasswordFailure(e.statusCode, e.message));
        return { success: false };
      });
  };
}

// Reset password
const resetPasswordAction = (token, password) => ({
  type: 'RESET_PASSWORD',
  token,
  password,
});

const resetPasswordSuccess = () => ({
  type: 'RESET_PASSWORD_SUCCESS',
});

const resetPasswordFailure = (statusCode, message) => ({
  type: 'RESET_PASSWORD_FAILURE',
  statusCode,
  message,
});

export function resetPassword(token, password) {
  return (dispatch) => {
    dispatch(resetPasswordAction(token, password));

    return userPasswordReset(token, password)
      .then(e => {
        if (e.statusCode === 200) {
          dispatch(resetPasswordSuccess());
          return { success: true };
        }
        dispatch(resetPasswordFailure(e.statusCode, e.message));
        return { success: false };
      })
      .catch(e => {
        dispatch(resetPasswordFailure(e.statusCode, e.message));
        return { success: false };
      });
  };
}
